import { Form, notification, Select } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { apiUrl } from "../App";

const { Option } = Select;

interface LocationProps {
  form: any;
  formProps: any;
}

const EditLocationForm: React.FC<LocationProps> = ({ form, formProps }) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  useEffect(() => {
    const initialCity = formProps?.initialValues?.city;
    const initialState = formProps?.initialValues?.state;
    const initialCountry = formProps?.initialValues?.country;

    if (initialCity && searchValue === undefined) {
      const fullLabel = [initialCity, initialState, initialCountry]
        .filter(Boolean)
        .join(", ");
      setSearchValue(fullLabel);
    }
  }, [formProps?.initialValues, searchValue]);

  const fetchPlaces = async (query: string) => {
    if (!query || query.length < 3) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/location/place`, {
        params: { query, placeType: "city" },
        headers: {
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });

      if (Array.isArray(response.data)) {
        setOptions(
          response.data.map((item: any) => ({
            label: item.label,
            value: item.placeId,
          })),
        );
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error("Error searching places:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (searchTimeout) clearTimeout(searchTimeout);

    if (value.length >= 3) {
      const timeout = setTimeout(() => {
        fetchPlaces(value);
      }, 400);
      setSearchTimeout(timeout);
    } else {
      setOptions([]);
    }
  };

  const handleSelect = async (placeId: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/location/place/${placeId}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });
      // response.data is { city, state, country, isoCode }
      const { city, state, country, isoCode, label } = response.data;

      form.setFieldsValue({
        city: city || "",
        state: state || "",
        country: isoCode || country || "",
      });

      // Update the search box to the full label of the selected place
      const selectedOption = options.find((o) => o.value === placeId);
      if (selectedOption) {
        setSearchValue(label);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching place details.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Form.Item label="City">
        <Select
          showSearch
          value={searchValue}
          placeholder="Search for a city..."
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={handleSearch}
          onSelect={handleSelect}
          notFoundContent={null}
          loading={loading}
          style={{ width: "100%" }}
        >
          {options.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
          {loading && (
            <Option value="loading" disabled>
              Loading...
            </Option>
          )}
        </Select>
      </Form.Item>

      {/* Hidden items to store the actual values in the form */}
      <Form.Item name="city" noStyle hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="state" noStyle hidden>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="country" noStyle hidden>
        <input type="hidden" />
      </Form.Item>
    </>
  );
};

export default EditLocationForm;
